import { Link } from 'react-router-dom';
import { Instagram, MapPin, Phone, Mail, Clock } from 'lucide-react';
import FlorealLogo from '@/components/FlorealLogo';
import HoursList from '@/components/HoursList';
import { useSite, useWhatsappUrl } from '@/context/SiteContext';
import { TEST_IDS } from '@/constants/testIds/floreal';

export default function Footer() {
    const { menu, settings, t } = useSite();
    const whatsappUrl = useWhatsappUrl();
    const menuItems = (menu || []).filter((m) => !m.hidden);
    const address = [settings.address_line1, settings.address_line2].filter(Boolean);
    const phones = [
        settings.phone_shop_value && { label: settings.phone_shop_label, value: settings.phone_shop_value, tel: settings.phone_shop_tel },
        settings.phone_mobile_value && { label: settings.phone_mobile_label, value: settings.phone_mobile_value, tel: settings.phone_mobile_tel },
    ].filter(Boolean);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const resolveLogo = (u) => {
        if (!u) return '';
        if (u.startsWith('http://') || u.startsWith('https://')) return u;
        if (u.startsWith('/')) return u;
        return `${backendUrl}${u}`;
    };
    const logoUrl = settings.logo_url_desktop || settings.logo_url_mobile || '';
    const logoWidth = Number(settings.logo_width_footer_px) || 200;
    const logoMaxH = Number(settings.logo_max_height_footer_px) || 90;

    return (
        <footer data-testid={TEST_IDS.footer} className="mt-24 border-t border-border bg-atelier-ivory">
            <div className="h-px w-24 bg-atelier-bronze-soft mx-auto -mt-px" />
            <div className="container-atelier py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                {/* Brand */}
                <div>
                    {logoUrl ? (
                        <img
                            src={resolveLogo(logoUrl)}
                            alt={settings.site_name || 'Floreal'}
                            className="mb-4 w-auto object-contain"
                            style={{ width: `${logoWidth}px`, maxHeight: `${logoMaxH}px` }}
                            data-testid="footer-logo-img"
                        />
                    ) : (
                        <FlorealLogo variant="full" className="text-3xl text-atelier-sage-ink mb-4" data-testid="footer-logo-svg" />
                    )}
                    <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
                        {t('footer.tagline', 'Composizioni floreali, allestimenti ed emozioni su misura. Consegne a domicilio.')}
                    </p>
                </div>

                {/* Atelier address + orari */}
                <div>
                    <p className="eyebrow mb-4">Atelier</p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                        {address.map((line) => <li key={line}>{line}</li>)}
                        {settings.google_maps_url && (
                            <li className="pt-2">
                                <a
                                    href={settings.google_maps_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={TEST_IDS.footerMaps}
                                    className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
                                >
                                    <MapPin className="h-3.5 w-3.5" />
                                    Apri su Maps
                                </a>
                            </li>
                        )}
                    </ul>
                    <p className="eyebrow mt-6 mb-3 inline-flex items-center gap-1.5"><Clock className="h-3 w-3" />Orari</p>
                    <div data-testid="footer-hours">
                        <HoursList compact />
                    </div>
                </div>

                {/* Contacts */}
                <div>
                    <p className="eyebrow mb-4">Contatti</p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                        {phones.map((p) => (
                            <li key={p.tel}>
                                <a href={`tel:${p.tel}`} className="hover:text-foreground transition-colors inline-flex items-center gap-1.5">
                                    <Phone className="h-3.5 w-3.5" />
                                    {p.value}
                                </a>
                            </li>
                        ))}
                        {settings.email && (
                            <li>
                                <a href={`mailto:${settings.email}`} className="hover:text-foreground transition-colors inline-flex items-center gap-1.5">
                                    <Mail className="h-3.5 w-3.5" />
                                    {settings.email}
                                </a>
                            </li>
                        )}
                    </ul>
                </div>

                {/* Social */}
                <div>
                    <p className="eyebrow mb-4">Seguici</p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                        {settings.instagram_url && (
                            <li>
                                <a href={settings.instagram_url} target="_blank" rel="noreferrer" data-testid={TEST_IDS.footerInstagram} className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors">
                                    <Instagram className="h-3.5 w-3.5" />
                                    {settings.instagram_handle || 'Instagram'}
                                </a>
                            </li>
                        )}
                        {settings.facebook_url && (<li><a href={settings.facebook_url} target="_blank" rel="noreferrer" className="hover:text-foreground transition-colors">Facebook</a></li>)}
                        {settings.tiktok_url && (<li><a href={settings.tiktok_url} target="_blank" rel="noreferrer" className="hover:text-foreground transition-colors">TikTok</a></li>)}
                        {settings.pinterest_url && (<li><a href={settings.pinterest_url} target="_blank" rel="noreferrer" className="hover:text-foreground transition-colors">Pinterest</a></li>)}
                        <li>
                            <a href={whatsappUrl} target="_blank" rel="noreferrer" data-testid={TEST_IDS.footerWhatsapp} className="hover:text-foreground transition-colors">
                                Scrivici su WhatsApp
                            </a>
                        </li>
                        <li className="pt-2">
                            <Link to="/admin" className="text-xs text-muted-foreground/60 hover:text-foreground transition-colors">
                                Area riservata
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-border">
                <div className="container-atelier py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
                    <p>© {new Date().getFullYear()} {settings.site_name || 'Floreal Atelier del Fiore'}. {t('footer.copyright_suffix', 'Tutti i diritti riservati.')} · <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link></p>
                    <p>{[settings.address_line1, settings.address_line2].filter(Boolean).join(' — ')}</p>
                </div>
            </div>
        </footer>
    );
}
